import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import { useTransportStyles } from "./sharedStyles";
import type { TrColumn, TrSubtable } from "./types";

/**
 * Untertabelle eines Feldes — die Form, die die Felddefinition mit
 * „Je Warengruppe: …", „Je Klausel: …" oder einer festen Liste von Maxima
 * beschreibt.
 *
 * Zwei Arten von Zeilen liegen nebeneinander:
 *  - **Katalogzeilen** aus `subtable.rows`. Sie stehen fest da und werden nur
 *    ausgefüllt; ihre Bezeichnung ist nicht editierbar.
 *  - **Eigene Zeilen**, sofern `addable`. Sie tragen eine interne Kennung
 *    (`_id`), damit das Bearbeiten einer Zeile nicht die Nachbarzeile trifft,
 *    wenn eine Bezeichnung noch leer ist.
 */

export type SubTableRow = Record<string, string>;

type Props = {
  spec: TrSubtable;
  rows: SubTableRow[];
  onChange: (rows: SubTableRow[]) => void;
  /** Kennung für die Beschriftung der Eingaben (Screenreader). */
  fieldId: string;
  disabled?: boolean;
};

let eigeneZeileZaehler = 0;
const neueZeilenId = () => `z${(eigeneZeileZaehler += 1)}-${Math.random().toString(36).slice(2, 7)}`;

const SubTable: React.FC<Props> = ({ spec, rows, onChange, fieldId, disabled }) => {
  const styles = useTransportStyles();
  const katalog = spec.rows ?? [];

  /**
   * Anzeigezeilen: erst der Katalog in seiner festen Reihenfolge, dann die
   * eigenen Zeilen in Eingabereihenfolge. Katalogzeilen ohne gespeicherten
   * Wert erscheinen als leere Zeile — sie sind Teil der Maske, nicht des
   * Wertes, und werden erst beim Ausfüllen gespeichert.
   */
  const eigene = rows.filter((r) => r._id);
  const katalogZeilen = katalog.map(
    (name) => rows.find((r) => !r._id && r.zeile === name) ?? { zeile: name },
  );
  const anzeige = [...katalogZeilen, ...eigene];

  const schreibe = (naechste: SubTableRow[]) => {
    // Leere Katalogzeilen nicht mitspeichern — sie ergeben sich aus der Maske.
    const gefiltert = naechste.filter((r) => {
      if (r._id) return true;
      return Object.entries(r).some(
        ([k, v]) => k !== "zeile" && String(v ?? "").trim() !== "",
      );
    });
    onChange(gefiltert);
  };

  const aendere = (index: number, key: string, wert: string) => {
    const naechste = anzeige.map((r, i) => (i === index ? { ...r, [key]: wert } : { ...r }));
    schreibe(naechste);
  };

  const zeileHinzufuegen = () => {
    schreibe([...anzeige, { _id: neueZeilenId(), zeile: "" }]);
  };

  const zeileEntfernen = (index: number) => {
    schreibe(anzeige.filter((_, i) => i !== index));
  };

  const zelle = (row: SubTableRow, index: number, col: TrColumn) => {
    const wert = String(row[col.key] ?? "");
    const label = `${spec.rowLabel} ${row.zeile || index + 1}, ${col.label}`;
    if (col.type === "select") {
      return (
        <select
          className={styles.cellInput}
          value={wert}
          disabled={disabled}
          aria-label={label}
          onChange={(e) => aendere(index, col.key, e.target.value)}
        >
          <option value="">—</option>
          {(col.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    const platzhalter =
      col.type === "eur" ? "0,00" : col.type === "percent" ? "0" : col.type === "duration" ? "z. B. 30 Tage" : "";
    return (
      <input
        className={styles.cellInput}
        type="text"
        inputMode={col.type === "eur" || col.type === "percent" ? "decimal" : undefined}
        value={wert}
        disabled={disabled}
        placeholder={platzhalter}
        aria-label={label}
        onChange={(e) => aendere(index, col.key, e.target.value)}
      />
    );
  };

  return (
    <div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} scope="col">
                {spec.rowLabel}
              </th>
              {spec.columns.map((c) => (
                <th key={c.key} className={styles.th} scope="col" style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
              {spec.addable && <th className={styles.th} aria-label="Zeile entfernen" />}
            </tr>
          </thead>
          <tbody>
            {anzeige.map((row, index) => {
              const eigeneZeile = Boolean(row._id);
              return (
                <tr key={row._id ?? `k-${row.zeile}`}>
                  <td className={mergeClasses(styles.td, styles.tdRow)}>
                    {eigeneZeile ? (
                      <input
                        className={styles.cellInput}
                        type="text"
                        value={String(row.zeile ?? "")}
                        disabled={disabled}
                        placeholder={spec.rowLabel}
                        aria-label={`${spec.rowLabel} ${index + 1}`}
                        onChange={(e) => aendere(index, "zeile", e.target.value)}
                      />
                    ) : (
                      row.zeile
                    )}
                  </td>
                  {spec.columns.map((c) => (
                    <td key={c.key} className={styles.td}>
                      {zelle(row, index, c)}
                    </td>
                  ))}
                  {spec.addable && (
                    <td className={styles.td}>
                      {eigeneZeile && (
                        <button
                          type="button"
                          className={styles.rowDeleteBtn}
                          disabled={disabled}
                          aria-label={`Zeile ${row.zeile || index + 1} entfernen`}
                          title="Zeile entfernen"
                          onClick={() => zeileEntfernen(index)}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {anzeige.length === 0 && (
              <tr>
                <td className={styles.td} colSpan={spec.columns.length + (spec.addable ? 2 : 1)}>
                  <span style={{ color: "#7c7480" }}>Noch keine Einträge.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {spec.addable && !disabled && (
        <div className={styles.tableActions}>
          <button type="button" className={styles.rowDeleteBtn} onClick={zeileHinzufuegen}>
            ＋ {spec.rowLabel} hinzufügen
          </button>
        </div>
      )}
    </div>
  );
};

export default SubTable;
